<template>
  <main class="auto-page">
    <CompanyBreadcrumb :aria-label="$t('general.auto_assignments')"
                       :home-aria-label="$t('general.home')"
                       :items="breadcrumbItems"
    />

    <template v-if="errorMessages">
      <div v-for="(error, index) in errorMessages" :key="index" class="uk-alert-danger" uk-alert>
        <a class="uk-alert-close" uk-close></a>
        <p style="color: red">
          <i class="uil-exclamation-triangle"></i> {{ error }}
        </p>
      </div>
    </template>
    <div v-if="successMessage" class="uk-alert-success" uk-alert>
      <a class="uk-alert-close" uk-close></a>
      <p style="color: green">
        <i class="uil-exclamation-triangle"></i> {{ successMessage }}
      </p>
    </div>

    <div class="auto-page-header">
      <h2 class="auto-page-title">
        {{ $t('general.all_auto_assignments') }} ({{ items.total }})
      </h2>
      <router-link :to="{ name: 'company.assignments.addAuto' }">
        <button class="btn btn-default auto-add-btn" type="button">
          <i class="uil-location-arrow"></i> {{ $t('general.create_auto_assignment') }}
        </button>
      </router-link>
    </div>

    <div class="card auto-card">
      <div class="auto-toolbar">
        <div class="auto-toolbar-search">
          <search-input v-model="filter.search"
                        :aria-label="$t('general.search')"
                        :control-height="48"
                        :debounce="0"
                        :placeholder="$t('general.search')"
          />
        </div>
        <div class="auto-toolbar-actions">
          <sort-dropdown :sort-options="sortOptions"
                         :title="$t('filters.sort_list')"
                         @sort-selected="onSortSelected"/>
        </div>
      </div>

      <default-loading v-if="programs_loading"></default-loading>
      <template v-else>
        <div class="sf-table sf-table--zebra sf-table--hover auto-table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">{{ $t('general.assignment_name') }}</th>
                <th scope="col">{{ $t("general.added_at") }}</th>
                <th class="sf-table__col-actions" scope="col"></th>
              </tr>
            </thead>
            <tbody class="list">
              <tr v-for="item in formattedItems" :key="item.id">
                <th scope="row">
                  <div class="sf-table__media">
                    <div class="sf-table__media-body">
                      <a class="sf-table__name"
                         @click="$router.push({ name: 'company.assignments.editAuto', params: { id: item.id } })">
                        {{ item.name }}
                      </a>
                    </div>
                  </div>
                </th>
                <td><i class="uil-calendar-alt"></i> {{ item.created_at }}</td>
                <td class="sf-table__col-actions">
                  <div class="sf-table__actions">
                    <router-link :to="{ name: 'company.assignments.editAuto', params: { id: item.id } }"
                      :uk-tooltip="$t('faq_categories.edit')"
                      class="btn btn-icon btn-hover btn-sm btn-circle">
                      <i class="uil-edit"></i>
                    </router-link>
                    <a href="javascript:void(0)"
                       :uk-tooltip="$t('general.delete')"
                       class="btn btn-icon btn-hover btn-sm btn-circle"
                       @click="removeItem(item.id)">
                      <i class="uil-minus-circle text-danger"></i>
                    </a>
                  </div>
                </td>
              </tr>
              <tr v-if="!formattedItems.length">
                <td class="sf-table__empty" colspan="3">{{ $t("general.no_assignments_found") }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <pagination v-if="pagination && pagination.hasOwnProperty('links')" :pagination="pagination"
          class="mt-2 mb-3 auto-pagination" @changePage="setPage($event)"></pagination>
      </template>

    </div>
  </main>
</template>

<script>
import Vue from "vue";
import DataTable from "@/assets/components/dataTable/DataTable";
import store from "@/core/services";
import Pagination from "@/assets/components/Pagination";
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";
import SearchInput from "@/view/components/inputs/SearchInput.vue";
import SortDropdown from "@/view/components/dropdown/SortDropdown.vue";

import module, {
  MODULE_NAME,
  ERROR,
  ITEMS,
  GET_ITEMS,
  DELETE_ITEM,
  LOADING,
  GET_AUTO_ASSIGNMENTS
} from "@/core/services/store/assignment.module";
import moment from "moment";
import Swal from "sweetalert2";
import { RESET_POINTS } from "@/core/services/store/company.module";
import {confirmDialogDelete} from "@/utils/confirmDialog";

export default {
  name: "AssignmentAuto",
  components: { DataTable, Pagination, DefaultLoading, CompanyBreadcrumb, SearchInput, SortDropdown },
  data() {
    return {
      pagination: null,
      activeItem: null,
      successMessage: null,
      errorMessages: [],
      page: 1,
      per_page: 10,
      filter: {
        search: null,
        sort: "id",
        order: "desc",
      },
    };
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }
    registerStoreModule(MODULE_NAME, module);
  },
  computed: {
    breadcrumbItems() {
      return [
        { name: this.$t('menu.company_dashboard.assignments'), route: { name: 'company.assignments.list' } },
        { name: this.$t('general.auto_assignments') },
      ];
    },
    sortOptions() {
      return [
        { key: "title-asc", sort: "title", order: "asc", label: this.$t("sorting.a_z") },
        { key: "title-desc", sort: "title", order: "desc", label: this.$t("sorting.z_a") },
        { key: "created_at-asc", sort: "created_at", order: "asc", label: this.$t("sorting.created_at_asc") },
        { key: "created_at-desc", sort: "created_at", order: "desc", label: this.$t("sorting.created_at_desc") },
      ];
    },
    items: {
      get() {
        return store.getters[MODULE_NAME + "/" + ITEMS];
      },
      set(value) { },
    },
    programs_loading: {
      get() {
        return store.getters[MODULE_NAME + "/" + LOADING];
      },
      set(value) { },
    },
    assignment_loading: {
      get() {
        return store.getters[MODULE_NAME + "/" + LOADING];
      },
      set(value) { },
    },
    error: {
      get() {
        return store.getters[MODULE_NAME + "/" + ERROR];
      },
      set(value) { },
    },
    formattedItems() {
      let results = [];
      if (this.items === null || this.items.length === 0) return [];
      this.pagination = this.items;

      this.items.data.forEach((currentItem) => {
        results.push({
          id: currentItem.id,
          name: currentItem.name,
          description: currentItem.description ? currentItem.description : "-",
          total_section: currentItem.sections_count,
          total_content: currentItem.contents_count,
          created_at: currentItem.created_at
            ? moment(currentItem.created_at).format("DD/MM/YYYY HH:mm:ss")
            : "-",
        });
      });

      return results;
    },
  },
  methods: {
    getResult() {
      let self = this;
      this.$store.dispatch(MODULE_NAME + "/" + GET_ITEMS, {
        url: 'api/automatic-assignments',
        filters: {
          like: self.filter.search,
          page: self.page,
          perPage: self.per_page,
          sort: self.filter.sort,
          order: self.filter.order,
        },
      });
    },
    async removeItem(id) {
      const isConfirmed = await confirmDialogDelete({context: this});

      if (isConfirmed) {
        this.$store
            .dispatch(MODULE_NAME + "/" + DELETE_ITEM, {
              url: 'api/automatic-assignments/' + id,
            })
            .then(() => {
              if (!this.error) {
                this.successMessage = this.$t("general.successfully_deleted");
                this.getResult();
              } else this.errorMessages.push(this.error);
            });
        }
    },
    // SortDropdown { sort, order } yayiyor; sortOrder'in imzasi da ayni
    // sirada oldugu icin tek satirlik adaptor yetiyor.
    onSortSelected({ sort, order }) {
      this.sortOrder(sort, order);
    },
    sortOrder(sort, order) {
      this.page = 1;
      this.filter.sort = sort;
      this.filter.order = order;
      this.getResult();
    },
    setPage(page) {
      this.page = page;
      this.getResult();
    },
    resetMessages() {
      this.successMessage = null;
      this.errorMessages = [];
    },
  },
  mounted() {
    this.getResult();
  },
  watch: {
    "filter.search": function () {
      this.debouncedFilterSearch();
    },
    successMessage: function () {
      this.debouncedResetValues();
    },
    errorMessages: function () {
      this.debouncedResetValues();
    },
  },
  created: function () {
    this.debouncedFilterSearch = _.debounce(this.getResult, 500);
    this.debouncedResetValues = _.debounce(this.resetMessages, 2000);
  },
};
</script>

<style scoped>
.auto-page {
  --auto-primary: var(--primaryColor, #3e416d);
  --auto-text: #1e293b;
  --auto-muted: #64748b;
  --auto-border: #e2e8f0;
  --auto-soft: #f6f7fb;
  min-width: 0;
}

/* ═══════ Page Header ═══════ */
.auto-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.auto-page-title {
  margin: 0;
  color: var(--auto-text);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3;
}

.auto-add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 17px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
  white-space: nowrap;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.auto-add-btn:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.auto-add-btn:active:not(:disabled) {
  transform: scale(0.98);
  opacity: 0.85;
}

/* ═══════ Card ═══════ */
.auto-card {
  overflow: hidden;
  margin-top: 0;
  border: 1px solid var(--auto-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* ═══════ Toolbar ═══════ */
.auto-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
}

.auto-toolbar-search {
  flex: 1 1 auto;
  min-width: 0;
}

.auto-toolbar-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

.auto-toolbar-actions ::v-deep .btn-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.auto-toolbar-actions ::v-deep .btn-circle:active {
  transform: scale(0.98);
  opacity: 0.85;
}

/* ═══════ Table ═══════ */
/* Tasarim .sf-table'dan gelir (assets/scss/_data-table.scss).
   Burada yalnizca sayfaya ozgu degerler ayarlanir. */

/* ═══════ Pagination ═══════ */
.auto-pagination {
  padding: 0 8px 8px;
}

input[type="button"],
input[type="submit"],
span.btn,
.btn {
  display: inline-block;
}

/* ═══════ Mobile: <960px ═══════ */
@media (max-width: 959px) {
  .auto-page {
    padding: 0 0 24px;
  }

  .auto-page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .auto-page-title {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.3;
  }

  .auto-add-btn {
    width: 100%;
    min-height: 48px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }

  .auto-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .auto-toolbar-search {
    width: 100%;
    max-width: none;
  }

  .auto-toolbar-actions {
    flex-wrap: wrap;
    align-self: flex-end;
    width: auto;
    max-width: 100%;
    margin-left: auto;
    justify-content: flex-end;
  }

  .auto-toolbar-actions ::v-deep .btn-circle {
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
  }

  /* Tablo mobilde sikistirilmaz, yatay kaydirilir */
  .auto-table-wrap {
    --sf-table-min-width: 640px;
  }
}

@media (max-width: 767px) {
  .auto-page {
    padding: 0;
  }

  .auto-page-title {
    font-size: 24px;
  }

  .auto-pagination {
    padding: 0 16px 16px;
  }
}

/* ═══════ Hover ═══════ */
@media (hover: hover) and (pointer: fine) {
  .auto-add-btn:hover:not(:disabled) {
    opacity: 0.92;
  }
}

@media (hover: none) {
  .auto-page ::v-deep .btn-hover:hover {
    background-color: transparent;
  }
}

/* ═══════ Reduced motion ═══════ */
@media (prefers-reduced-motion: reduce) {
  .auto-add-btn:active,
  .auto-toolbar-actions ::v-deep .btn-circle:active {
    transform: none;
  }
}
</style>
